/**
 * PortalUnavailableBanner — a red bar across the top of the shell while the portal monitor
 * cannot reach Occurra Cloud. It stays until the cloud answers: an operator who could hide it
 * would see failing panels with no reason.
 */

import { CloudOff } from 'lucide-react';
import type { ReactElement } from 'react';

import { Button } from '@/components/occurra/Button';
import { usePortalUnavailable } from '@/hooks/usePortalUnavailable';
import { cn } from '@/lib/utils';

export const PortalUnavailableBanner = (): ReactElement | null => {
    const { isUnavailable, tryNow } = usePortalUnavailable();
    if (!isUnavailable) {
        return null;
    }
    return (
        <div
            role="alert"
            className="shrink-0 bg-occ-background-panel shadow-md"
        >
            <div className={cn(
                'flex items-center gap-3 px-4 py-2.5',
                'bg-occ-red-200 text-sm font-medium text-occ-foreground-high-contrast',
                'pt-[max(0.625rem,env(safe-area-inset-top))]',
            )}
            >
                <CloudOff className="size-5 shrink-0" />
                <span className="min-w-0 flex-1">
                    Cannot reach Occurra Cloud. Reconnecting…
                </span>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={tryNow}
                    className="shrink-0"
                >
                    Try now
                </Button>
            </div>
        </div>
    );
};
